export const MULTILINGUAL_SPEAKER_LANGUAGE = 'mul'
export const MULTILINGUAL_SPEAKER_LANGUAGE_CODES = [
  'en',
  'es',
  'de',
  'fr',
  'pt',
  'it',
] as const
export const ALL_TARGET_LANGUAGES = 'ALL'
const DEFAULT_CAPTION_LANGUAGE = 'en'
const EQUIVALENT_TARGET_LANGUAGE_BY_SPEAKER_LANGUAGE: Readonly<
  Record<string, string>
> = {
  es: 'es-ES',
  fr: 'fr-FR',
  pt: 'pt-PT',
}
const SCOTLAND_FLAG_EMOJI = '\u{1F3F4}\u{E0067}\u{E0062}\u{E0073}\u{E0063}\u{E0074}\u{E007F}'
const WALES_FLAG_EMOJI = '\u{1F3F4}\u{E0067}\u{E0062}\u{E0077}\u{E006C}\u{E0073}\u{E007F}'

export type CaptionTtsPipeline = 'remote'

export interface CaptionTtsCapability {
  pipeline: CaptionTtsPipeline
  languageCodes: readonly string[]
}

export interface PublicCaptionLanguage {
  code: string
  name: string
  nativeName?: string
  emoji: string
  rtl?: boolean
  /** Ordered exact Expo Speech language tags that are safe for this target. */
  localSpeechLanguageCodes?: string[]
  ttsCapabilities?: readonly CaptionTtsCapability[]
  /** Source language codes covered by this automatic-detection option. */
  automaticDetectionLanguageCodes?: readonly string[]
}

export type PublicTargetCaptionLanguage = PublicCaptionLanguage & {
  nativeName: string
}

export type CaptionTtsLookup =
  | string
  | (Partial<PublicCaptionLanguage> & {
      code?: string | null
      ttsCapabilities?: readonly CaptionTtsCapability[] | null
    })
  | null
  | undefined

const cleanSpeechLanguageCodes = (languageCodes: readonly string[]): string[] =>
  Array.from(
    new Set(languageCodes.map((code) => code.trim()).filter((code) => !!code)),
  )

const cleanCaptionTtsCapabilities = (
  capabilities: readonly CaptionTtsCapability[],
): CaptionTtsCapability[] => {
  const languageCodes = cleanSpeechLanguageCodes(
    capabilities.flatMap((capability) =>
      capability.pipeline.trim() === 'remote' &&
      Array.isArray(capability.languageCodes)
        ? capability.languageCodes
        : [],
    ),
  )

  return languageCodes.length > 0 ? [{ pipeline: 'remote', languageCodes }] : []
}

export function getCaptionTtsCapabilities(
  language: CaptionTtsLookup,
): readonly CaptionTtsCapability[] {
  if (language && typeof language === 'object' && Array.isArray(language.ttsCapabilities)) {
    return cleanCaptionTtsCapabilities(language.ttsCapabilities)
  }

  return []
}

export function hasRemoteCaptionSpeech(
  language: CaptionTtsLookup,
): boolean {
  return getCaptionTtsCapabilities(language).length > 0
}

function withTargetCaptionLanguageMetadata<TLanguage extends PublicTargetCaptionLanguage>(
  languages: TLanguage[],
): TLanguage[] {
  return languages.map((language) => {
    const localSpeechLanguageCodes = cleanSpeechLanguageCodes(
      language.localSpeechLanguageCodes || [],
    )

    return {
      ...language,
      ...(localSpeechLanguageCodes.length > 0 ? { localSpeechLanguageCodes } : {}),
    }
  })
}

export const TARGET_CAPTION_LANGUAGES: readonly PublicTargetCaptionLanguage[] =
  withTargetCaptionLanguageMetadata([
    { code: 'ab', name: 'Abkhaz', nativeName: 'Аҧсуа', emoji: '🌐' },
    { code: 'ace', name: 'Acehnese', nativeName: 'bahsa Acèh', emoji: '🇮🇩' },
    { code: 'ach', name: 'Acholi', nativeName: 'Acholi', emoji: '🇺🇬' },
    { code: 'af', name: 'Afrikaans', nativeName: 'Afrikaans', emoji: '🇿🇦' },
    { code: 'ak', name: 'Akan', nativeName: 'Akan', emoji: '🇬🇭' },
    { code: 'sq', name: 'Albanian', nativeName: 'Shqip', emoji: '🇦🇱', localSpeechLanguageCodes: ['sq-AL'] },
    { code: 'alz', name: 'Alur', nativeName: 'Alur', emoji: '🇺🇬' },
    { code: 'am', name: 'Amharic', nativeName: 'አማርኛ', emoji: '🇪🇹' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', emoji: '🌐', rtl: true, localSpeechLanguageCodes: ['ar-001', 'ar'] },
    { code: 'hy', name: 'Armenian', nativeName: 'Հայերեն', emoji: '🇦🇲' },
    { code: 'as', name: 'Assamese', nativeName: 'অসমীয়া', emoji: '🇮🇳', localSpeechLanguageCodes: ['as-IN'] },
    { code: 'awa', name: 'Awadhi', nativeName: 'अवधी', emoji: '🇮🇳' },
    { code: 'ay', name: 'Aymara', nativeName: 'Aymar aru', emoji: '🇧🇴' },
    { code: 'az', name: 'Azerbaijani', nativeName: 'Azərbaycanca', emoji: '🇦🇿' },
    { code: 'ban', name: 'Balinese', nativeName: 'Basa Bali', emoji: '🇮🇩' },
    { code: 'bm', name: 'Bambara', nativeName: 'Bamanankan', emoji: '🇲🇱' },
    { code: 'ba', name: 'Bashkir', nativeName: 'Башҡортса', emoji: '🇷🇺' },
    { code: 'eu', name: 'Basque', nativeName: 'Euskara', emoji: '🇪🇸', localSpeechLanguageCodes: ['eu-ES'] },
    { code: 'btx', name: 'Batak Karo', nativeName: 'Cakap Karo', emoji: '🇮🇩' },
    { code: 'bts', name: 'Batak Simalungun', nativeName: 'Hata Simalungun', emoji: '🇮🇩' },
    { code: 'bbc', name: 'Batak Toba', nativeName: 'Hata Batak Toba', emoji: '🇮🇩' },
    { code: 'be', name: 'Belarusian', nativeName: 'Беларуская', emoji: '🌐' },
    { code: 'bem', name: 'Bemba', nativeName: 'Ichibemba', emoji: '🇿🇲' },
    { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', emoji: '🇧🇩', localSpeechLanguageCodes: ['bn-IN', 'bn-BD'] },
    { code: 'bew', name: 'Betawi', nativeName: 'basa Betawi', emoji: '🇮🇩' },
    { code: 'bho', name: 'Bhojpuri', nativeName: 'भोजपुरी', emoji: '🇮🇳', localSpeechLanguageCodes: ['bho-IN'] },
    { code: 'bik', name: 'Bikol', nativeName: 'Bikol', emoji: '🇵🇭' },
    { code: 'brx', name: 'Bodo', nativeName: 'बड़ो', emoji: '🇮🇳', localSpeechLanguageCodes: ['brx-IN'] },
    { code: 'bs', name: 'Bosnian', nativeName: 'Bosanski', emoji: '🇧🇦', localSpeechLanguageCodes: ['bs-BA'] },
    { code: 'br', name: 'Breton', nativeName: 'Brezhoneg', emoji: '🇫🇷' },
    { code: 'bg', name: 'Bulgarian', nativeName: 'Български', emoji: '🇧🇬', localSpeechLanguageCodes: ['bg-BG'] },
    { code: 'bua', name: 'Buryat', nativeName: 'Буряад хэлэн', emoji: '🇷🇺' },
    { code: 'yue', name: 'Cantonese', nativeName: '粵語', emoji: '🇭🇰', localSpeechLanguageCodes: ['yue-HK'] },
    { code: 'ca', name: 'Catalan', nativeName: 'Català', emoji: '🇪🇸', localSpeechLanguageCodes: ['ca-ES'] },
    { code: 'ceb', name: 'Cebuano', nativeName: 'Sinugboanon', emoji: '🇵🇭' },
    { code: 'ny', name: 'Chichewa (Nyanja)', nativeName: 'Chichewa', emoji: '🇲🇼' },
    { code: 'zh-Hans', name: 'Chinese (Simplified)', nativeName: '中文（简体）', emoji: '🇨🇳', localSpeechLanguageCodes: ['zh-CN'] },
    { code: 'zh-Hant', name: 'Chinese (Traditional)', nativeName: '中文（繁體）', emoji: '🇹🇼', localSpeechLanguageCodes: ['zh-TW'] },
    { code: 'cv', name: 'Chuvash', nativeName: 'Чӑвашла', emoji: '🇷🇺' },
    { code: 'co', name: 'Corsican', nativeName: 'Corsu', emoji: '🇫🇷' },
    { code: 'crh', name: 'Crimean Tatar', nativeName: 'Qırımtatar tili', emoji: '🇺🇦' },
    { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski', emoji: '🇭🇷', localSpeechLanguageCodes: ['hr-HR'] },
    { code: 'cs', name: 'Czech', nativeName: 'Čeština', emoji: '🇨🇿', localSpeechLanguageCodes: ['cs-CZ'] },
    { code: 'da', name: 'Danish', nativeName: 'Dansk', emoji: '🇩🇰', localSpeechLanguageCodes: ['da-DK'] },
    { code: 'prs', name: 'Dari', nativeName: 'دری', emoji: '🇦🇫', rtl: true },
    { code: 'din', name: 'Dinka', nativeName: 'Thuɔŋjäŋ', emoji: '🇸🇸' },
    { code: 'dv', name: 'Divehi', nativeName: 'ދިވެހި', emoji: '🇲🇻', rtl: true },
    { code: 'doi', name: 'Dogri', nativeName: 'डोगरी', emoji: '🇮🇳', localSpeechLanguageCodes: ['doi-IN'] },
    { code: 'dov', name: 'Dombe', nativeName: 'Dombe', emoji: '🇦🇴' },
    { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', emoji: '🇳🇱', localSpeechLanguageCodes: ['nl-NL', 'nl-BE'] },
    { code: 'dz', name: 'Dzongkha', nativeName: 'རྫོང་ཁ', emoji: '🇧🇹' },
    { code: 'mhr', name: 'Eastern Mari (Meadow Mari)', nativeName: 'марий йылме', emoji: '🇷🇺' },
    { code: 'en', name: 'English', nativeName: 'English', emoji: '🌎', localSpeechLanguageCodes: ['en-US', 'en-GB', 'en-AU', 'en-IN', 'en-IE', 'en-ZA', 'en-NG'] },
    { code: 'eo', name: 'Esperanto', nativeName: 'Esperanto', emoji: '🌐' },
    { code: 'et', name: 'Estonian', nativeName: 'Eesti', emoji: '🇪🇪', localSpeechLanguageCodes: ['et-EE'] },
    { code: 'ee', name: 'Ewe', nativeName: 'Eʋegbe', emoji: '🇬🇭' },
    { code: 'fj', name: 'Fijian', nativeName: 'vosa Vakaviti', emoji: '🇫🇯' },
    { code: 'fi', name: 'Finnish', nativeName: 'Suomi', emoji: '🇫🇮', localSpeechLanguageCodes: ['fi-FI'] },
    { code: 'fr-FR', name: 'French (France)', nativeName: 'Français (France)', emoji: '🇫🇷', localSpeechLanguageCodes: ['fr-FR'] },
    { code: 'fr-CA', name: 'French (Canada)', nativeName: 'Français (Canada)', emoji: '🇨🇦', localSpeechLanguageCodes: ['fr-CA'] },
    { code: 'ff', name: 'Fulfulde', nativeName: 'Fulfulde', emoji: '🇳🇬' },
    { code: 'gaa', name: 'Ga', nativeName: 'Gã', emoji: '🇬🇭' },
    { code: 'gl', name: 'Galician', nativeName: 'Galego', emoji: '🇪🇸', localSpeechLanguageCodes: ['gl-ES'] },
    { code: 'lg', name: 'Ganda (Luganda)', nativeName: 'Luganda', emoji: '🇺🇬' },
    { code: 'ka', name: 'Georgian', nativeName: 'ქართული', emoji: '🇬🇪' },
    { code: 'de', name: 'German', nativeName: 'Deutsch', emoji: '🇩🇪', localSpeechLanguageCodes: ['de-DE'] },
    { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', emoji: '🇬🇷', localSpeechLanguageCodes: ['el-GR'] },
    { code: 'gn', name: 'Guarani', nativeName: 'Avañe\'ẽ', emoji: '🇵🇾' },
    { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', emoji: '🇮🇳', localSpeechLanguageCodes: ['gu-IN'] },
    { code: 'ht', name: 'Haitian Creole', nativeName: 'Kreyòl ayisyen', emoji: '🇭🇹' },
    { code: 'cnh', name: 'Hakha Chin', nativeName: 'Laiholh', emoji: '🌐' },
    { code: 'ha', name: 'Hausa', nativeName: 'Hausa', emoji: '🇳🇬' },
    { code: 'he', name: 'Hebrew', nativeName: 'עברית', emoji: '🇮🇱', rtl: true, localSpeechLanguageCodes: ['he-IL', 'iw-IL'] },
    { code: 'hil', name: 'Hiligaynon', nativeName: 'Hiligaynon', emoji: '🇵🇭' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', emoji: '🇮🇳', localSpeechLanguageCodes: ['hi-IN'] },
    { code: 'hmn', name: 'Hmong', nativeName: 'Hmoob', emoji: '🌐' },
    { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', emoji: '🇭🇺', localSpeechLanguageCodes: ['hu-HU'] },
    { code: 'hrx', name: 'Hunsrik', nativeName: 'Hunsrik', emoji: '🇩🇪' },
    { code: 'is', name: 'Icelandic', nativeName: 'Íslenska', emoji: '🇮🇸', localSpeechLanguageCodes: ['is-IS'] },
    { code: 'ig', name: 'Igbo', nativeName: 'Igbo', emoji: '🇳🇬' },
    { code: 'ilo', name: 'Ilocano', nativeName: 'Ilokano', emoji: '🇵🇭' },
    { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', emoji: '🇮🇩', localSpeechLanguageCodes: ['id-ID', 'in-ID'] },
    { code: 'ga', name: 'Irish', nativeName: 'Gaeilge', emoji: '🇮🇪' },
    { code: 'it', name: 'Italian', nativeName: 'Italiano', emoji: '🇮🇹', localSpeechLanguageCodes: ['it-IT'] },
    { code: 'ja', name: 'Japanese', nativeName: '日本語', emoji: '🇯🇵', localSpeechLanguageCodes: ['ja-JP'] },
    { code: 'jv', name: 'Javanese', nativeName: 'Basa Jawa', emoji: '🇮🇩', localSpeechLanguageCodes: ['jv-ID'] },
    { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', emoji: '🇮🇳', localSpeechLanguageCodes: ['kn-IN'] },
    { code: 'pam', name: 'Kapampangan (Pampanga)', nativeName: 'Kapampángan', emoji: '🇵🇭' },
    { code: 'ks', name: 'Kashmiri', nativeName: 'کٲشُر', emoji: '🌐', rtl: true, localSpeechLanguageCodes: ['ks-IN'] },
    { code: 'kk', name: 'Kazakh', nativeName: 'қазақ тілі', emoji: '🇰🇿' },
    { code: 'km', name: 'Khmer', nativeName: 'ភាសាខ្មែរ', emoji: '🇰🇭', localSpeechLanguageCodes: ['km-KH'] },
    { code: 'cgg', name: 'Kiga', nativeName: 'Rukiga', emoji: '🇺🇬' },
    { code: 'rw-x-mulenge', name: 'Kinyamulenge', nativeName: 'Ikinyamulenge', emoji: '🌐' },
    { code: 'rw', name: 'Kinyarwanda', nativeName: 'Ikinyarwanda', emoji: '🇷🇼' },
    { code: 'ktu', name: 'Kituba', nativeName: 'Kituba', emoji: '🇨🇬' },
    { code: 'gom', name: 'Konkani (Goan)', nativeName: 'कोंकणी', emoji: '🇮🇳', localSpeechLanguageCodes: ['gom-IN', 'kok-IN'] },
    { code: 'ko', name: 'Korean', nativeName: '한국어', emoji: '🇰🇷', localSpeechLanguageCodes: ['ko-KR'] },
    { code: 'kri', name: 'Krio', nativeName: 'Krio', emoji: '🇸🇱' },
    { code: 'kmr', name: 'Kurdish (Kurmanji)', nativeName: 'Kurdî', emoji: '🌐' },
    { code: 'ckb', name: 'Kurdish (Sorani)', nativeName: 'سۆرانی', emoji: '🌐', rtl: true },
    { code: 'ky', name: 'Kyrgyz', nativeName: 'Кыргызча', emoji: '🇰🇬' },
    { code: 'lo', name: 'Lao', nativeName: 'ພາສາລາວ', emoji: '🇱🇦' },
    { code: 'ltg', name: 'Latgalian', nativeName: 'latgaļu volūda', emoji: '🇱🇻' },
    { code: 'la', name: 'Latin', nativeName: 'Lingua latina', emoji: '🇻🇦' },
    { code: 'lv', name: 'Latvian', nativeName: 'latviešu valoda', emoji: '🇱🇻', localSpeechLanguageCodes: ['lv-LV'] },
    { code: 'lij', name: 'Ligurian', nativeName: 'Ligure', emoji: '🇮🇹' },
    { code: 'li', name: 'Limburgish', nativeName: 'Limburgs', emoji: '🇳🇱' },
    { code: 'ln', name: 'Lingala', nativeName: 'Lingála', emoji: '🇨🇩' },
    { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių kalba', emoji: '🇱🇹', localSpeechLanguageCodes: ['lt-LT'] },
    { code: 'lmo', name: 'Lombard', nativeName: 'Lombard', emoji: '🇮🇹' },
    { code: 'luo', name: 'Luo', nativeName: 'Dholuo', emoji: '🇰🇪' },
    { code: 'lb', name: 'Luxembourgish', nativeName: 'Lëtzebuergesch', emoji: '🇱🇺' },
    { code: 'mk', name: 'Macedonian', nativeName: 'Македонски', emoji: '🇲🇰' },
    { code: 'mai', name: 'Maithili', nativeName: 'मैथिली', emoji: '🇮🇳', localSpeechLanguageCodes: ['mai-IN'] },
    { code: 'mak', name: 'Makassar', nativeName: 'Basa Mangkasaraʼ', emoji: '🇮🇩' },
    { code: 'mg', name: 'Malagasy', nativeName: 'Malagasy', emoji: '🇲🇬' },
    { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', emoji: '🇲🇾', localSpeechLanguageCodes: ['ms-MY'] },
    { code: 'ms-Arab', name: 'Malay (Jawi)', nativeName: 'بهاس ملايو', emoji: '🇲🇾', rtl: true },
    { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', emoji: '🇮🇳', localSpeechLanguageCodes: ['ml-IN'] },
    { code: 'mt', name: 'Maltese', nativeName: 'Malti', emoji: '🇲🇹' },
    { code: 'mi', name: 'Māori', nativeName: 'Māori', emoji: '🇳🇿' },
    { code: 'mr', name: 'Marathi', nativeName: 'मराठी', emoji: '🇮🇳', localSpeechLanguageCodes: ['mr-IN'] },
    { code: 'mni-Mtei', name: 'Meiteilon (Manipuri)', nativeName: 'ꯃꯤꯇꯩ ꯂꯣꯟ', emoji: '🇮🇳', localSpeechLanguageCodes: ['mni-IN'] },
    { code: 'min', name: 'Minang', nativeName: 'bahaso Minangkabau', emoji: '🇮🇩' },
    { code: 'lus', name: 'Mizo', nativeName: 'Mizo ṭawng', emoji: '🇮🇳' },
    { code: 'mn', name: 'Mongolian', nativeName: 'Монгол хэл', emoji: '🇲🇳' },
    { code: 'my', name: 'Myanmar (Burmese)', nativeName: 'မြန်မာဘာသာ', emoji: '🇲🇲' },
    { code: 'nr', name: 'Ndebele (South)', nativeName: 'isiNdebele', emoji: '🇿🇦' },
    { code: 'new', name: 'Nepalbhasa (Newari)', nativeName: 'नेवाः भाय्', emoji: '🇳🇵' },
    { code: 'ne', name: 'Nepali', nativeName: 'नेपाली', emoji: '🇳🇵', localSpeechLanguageCodes: ['ne-NP'] },
    { code: 'nso', name: 'Northern Sotho (Sepedi)', nativeName: 'Sepedi', emoji: '🇿🇦' },
    { code: 'no', name: 'Norwegian', nativeName: 'Norsk', emoji: '🇳🇴', localSpeechLanguageCodes: ['nb-NO'] },
    { code: 'nus', name: 'Nuer', nativeName: 'Thok Nath', emoji: '🇸🇸' },
    { code: 'oc', name: 'Occitan', nativeName: 'lenga d\'òc', emoji: '🇫🇷' },
    { code: 'or', name: 'Odia (Oriya)', nativeName: 'ଓଡ଼ିଆ', emoji: '🇮🇳', localSpeechLanguageCodes: ['or-IN'] },
    { code: 'om', name: 'Oromo', nativeName: 'Afaan Oromoo', emoji: '🇪🇹' },
    { code: 'pag', name: 'Pangasinan', nativeName: 'Pangasinan', emoji: '🇵🇭' },
    { code: 'pap', name: 'Papiamento', nativeName: 'Papiamentu', emoji: '🇦🇼' },
    { code: 'ps', name: 'Pashto', nativeName: 'پښتو', emoji: '🇦🇫', rtl: true },
    { code: 'fa', name: 'Persian (Farsi)', nativeName: 'فارسی', emoji: '🌐', rtl: true, localSpeechLanguageCodes: ['fa-IR'] },
    { code: 'pl', name: 'Polish', nativeName: 'Polski', emoji: '🇵🇱', localSpeechLanguageCodes: ['pl-PL'] },
    { code: 'pt-PT', name: 'Portuguese (Portugal)', nativeName: 'Português (Portugal)', emoji: '🇵🇹', localSpeechLanguageCodes: ['pt-PT'] },
    { code: 'pt-BR', name: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)', emoji: '🇧🇷', localSpeechLanguageCodes: ['pt-BR'] },
    { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', emoji: '🇮🇳', localSpeechLanguageCodes: ['pa-IN', 'pa-Guru-IN'] },
    { code: 'pa-Arab', name: 'Punjabi (Shahmukhi)', nativeName: 'پنجابی', emoji: '🇵🇰', rtl: true, localSpeechLanguageCodes: ['pa-Arab-PK', 'pa-PK'] },
    { code: 'qu', name: 'Quechua', nativeName: 'Qhichwa simi', emoji: '🇵🇪' },
    { code: 'rom', name: 'Romani', nativeName: 'Romani chib', emoji: '🌐' },
    { code: 'ro', name: 'Romanian', nativeName: 'Română', emoji: '🇷🇴', localSpeechLanguageCodes: ['ro-RO'] },
    { code: 'rn', name: 'Rundi', nativeName: 'Ikirundi', emoji: '🇧🇮' },
    { code: 'ru', name: 'Russian', nativeName: 'Русский', emoji: '🇷🇺', localSpeechLanguageCodes: ['ru-RU'] },
    { code: 'sm', name: 'Samoan', nativeName: 'Gagana Samoa', emoji: '🇼🇸' },
    { code: 'sg', name: 'Sango', nativeName: 'Sängö', emoji: '🇨🇫' },
    { code: 'sa', name: 'Sanskrit', nativeName: 'संस्कृतम्', emoji: '🇮🇳', localSpeechLanguageCodes: ['sa-IN'] },
    { code: 'sat', name: 'Santali', nativeName: 'ᱥᱟᱱᱛᱟᱲᱤ', emoji: '🇮🇳', localSpeechLanguageCodes: ['sat-IN'] },
    { code: 'gd', name: 'Scottish Gaelic', nativeName: 'Gàidhlig', emoji: SCOTLAND_FLAG_EMOJI },
    { code: 'sr', name: 'Serbian', nativeName: 'српски', emoji: '🇷🇸', localSpeechLanguageCodes: ['sr-RS'] },
    { code: 'st', name: 'Sesotho', nativeName: 'Sesotho', emoji: '🇱🇸' },
    { code: 'crs', name: 'Seychellois Creole', nativeName: 'Seselwa', emoji: '🇸🇨' },
    { code: 'shn', name: 'Shan', nativeName: 'တႆး', emoji: '🌐' },
    { code: 'sn', name: 'Shona', nativeName: 'chiShona', emoji: '🇿🇼' },
    { code: 'scn', name: 'Sicilian', nativeName: 'Sicilianu', emoji: '🇮🇹' },
    { code: 'szl', name: 'Silesian', nativeName: 'Ślōnskŏ gŏdka', emoji: '🇵🇱' },
    { code: 'sd', name: 'Sindhi', nativeName: 'سنڌي', emoji: '🇵🇰', rtl: true, localSpeechLanguageCodes: ['sd-IN'] },
    { code: 'si', name: 'Sinhala (Sinhalese)', nativeName: 'සිංහල', emoji: '🇱🇰', localSpeechLanguageCodes: ['si-LK'] },
    { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', emoji: '🇸🇰', localSpeechLanguageCodes: ['sk-SK'] },
    { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina', emoji: '🇸🇮', localSpeechLanguageCodes: ['sl-SI'] },
    { code: 'so', name: 'Somali', nativeName: 'Soomaali', emoji: '🇸🇴' },
    { code: 'es-ES', name: 'Spanish (Spain)', nativeName: 'Español (España)', emoji: '🇪🇸', localSpeechLanguageCodes: ['es-ES'] },
    {
      code: 'es-419',
      name: 'Spanish (Latin America)',
      nativeName: 'Español latinoamericano',
      emoji: '🌐',
      localSpeechLanguageCodes: ['es-MX', 'es-US'],
    },
    { code: 'su', name: 'Sundanese', nativeName: 'Basa Sunda', emoji: '🇮🇩', localSpeechLanguageCodes: ['su-ID'] },
    { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', emoji: '🇰🇪', localSpeechLanguageCodes: ['sw-KE'] },
    { code: 'ss', name: 'Swati', nativeName: 'siSwati', emoji: '🇸🇿' },
    { code: 'sv', name: 'Swedish', nativeName: 'Svenska', emoji: '🇸🇪', localSpeechLanguageCodes: ['sv-SE'] },
    { code: 'fil', name: 'Tagalog (Filipino)', nativeName: 'Filipino', emoji: '🇵🇭', localSpeechLanguageCodes: ['fil-PH'] },
    { code: 'tg', name: 'Tajik', nativeName: 'тоҷикӣ', emoji: '🇹🇯' },
    { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', emoji: '🇮🇳', localSpeechLanguageCodes: ['ta-IN'] },
    { code: 'tt', name: 'Tatar', nativeName: 'татар теле', emoji: '🇷🇺' },
    { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', emoji: '🇮🇳', localSpeechLanguageCodes: ['te-IN'] },
    { code: 'tet', name: 'Tetum', nativeName: 'Lia-tetun', emoji: '🇹🇱' },
    { code: 'th', name: 'Thai', nativeName: 'ภาษาไทย', emoji: '🇹🇭', localSpeechLanguageCodes: ['th-TH'] },
    { code: 'ti', name: 'Tigrinya', nativeName: 'ትግርኛ', emoji: '🇪🇷' },
    { code: 've', name: 'Tshivenda', nativeName: 'Tshivenda', emoji: '🇿🇦' },
    { code: 'ts', name: 'Tsonga', nativeName: 'XiTsonga', emoji: '🇿🇦' },
    { code: 'tn', name: 'Tswana', nativeName: 'Setswana', emoji: '🇧🇼' },
    { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', emoji: '🇹🇷', localSpeechLanguageCodes: ['tr-TR'] },
    { code: 'tk', name: 'Turkmen', nativeName: 'Türkmençe', emoji: '🇹🇲' },
    { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', emoji: '🇺🇦', localSpeechLanguageCodes: ['uk-UA'] },
    { code: 'ur-PK', name: 'Urdu (Pakistan)', nativeName: 'اردو', emoji: '🇵🇰', rtl: true, localSpeechLanguageCodes: ['ur-PK'] },
    { code: 'ur-IN', name: 'Urdu (India)', nativeName: 'اردو', emoji: '🇮🇳', rtl: true, localSpeechLanguageCodes: ['ur-IN'] },
    { code: 'ug', name: 'Uyghur', nativeName: 'ئۇيغۇرچە', emoji: '🌐' },
    { code: 'uz', name: 'Uzbek', nativeName: 'Oʻzbek tili', emoji: '🇺🇿' },
    { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', emoji: '🇻🇳', localSpeechLanguageCodes: ['vi-VN'] },
    { code: 'fy', name: 'Western Frisian', nativeName: 'Frysk', emoji: '🇳🇱' },
    { code: 'cy', name: 'Welsh', nativeName: 'Cymraeg', emoji: WALES_FLAG_EMOJI, localSpeechLanguageCodes: ['cy-GB'] },
    { code: 'xh', name: 'Xhosa', nativeName: 'isiXhosa', emoji: '🇿🇦' },
    { code: 'yi', name: 'Yiddish', nativeName: 'ייִדיש', emoji: '🌐', rtl: true },
    { code: 'yo', name: 'Yoruba', nativeName: 'Yorùbá', emoji: '🇳🇬' },
    { code: 'yua', name: 'Yucatec Maya', nativeName: 'màaya t\'àan', emoji: '🇲🇽' },
    { code: 'zu', name: 'Zulu', nativeName: 'isiZulu', emoji: '🇿🇦' },
  ])

export const TARGET_LANGUAGES = TARGET_CAPTION_LANGUAGES

const TARGET_CAPTION_LANGUAGE_BY_CODE = new Map(
  TARGET_CAPTION_LANGUAGES.map((language) => [language.code, language]),
)

type PublicSpeakerLanguageMetadata = Pick<
  PublicCaptionLanguage,
  'code' | 'name' | 'nativeName' | 'emoji' | 'rtl' | 'automaticDetectionLanguageCodes'
>

const SPEAKER_LANGUAGE_METADATA_OVERRIDES: Record<
  string,
  PublicSpeakerLanguageMetadata
> = {
  [MULTILINGUAL_SPEAKER_LANGUAGE]: {
    code: MULTILINGUAL_SPEAKER_LANGUAGE,
    name: `Multilingual (${MULTILINGUAL_SPEAKER_LANGUAGE_CODES.map((code) =>
      code.toUpperCase(),
    ).join(', ')})`,
    nativeName: 'Multilingual',
    emoji: '🌐',
    automaticDetectionLanguageCodes: MULTILINGUAL_SPEAKER_LANGUAGE_CODES,
  },
  es: { code: 'es', name: 'Spanish (General)', nativeName: 'Español', emoji: '🇪🇸' },
  fr: { code: 'fr', name: 'French (General)', nativeName: 'Français', emoji: '🇫🇷' },
  pt: { code: 'pt', name: 'Portuguese (General)', nativeName: 'Português', emoji: '🇵🇹' },
  de: { code: 'de', name: 'German (General)', nativeName: 'Deutsch', emoji: '🇩🇪' },
  nl: { code: 'nl', name: 'Dutch (General)', nativeName: 'Nederlands', emoji: '🇳🇱' },
  'fr-CA': { code: 'fr-CA', name: 'French (Canada)', nativeName: 'Français (Canada)', emoji: '🇨🇦' },
  'nl-BE': { code: 'nl-BE', name: 'Flemish (Belgium)', nativeName: 'Vlaams (België)', emoji: '🇧🇪' },
  'de-CH': { code: 'de-CH', name: 'German (Switzerland)', nativeName: 'Deutsch (Schweiz)', emoji: '🇨🇭' },
  'pt-PT': { code: 'pt-PT', name: 'Portuguese (Portugal)', nativeName: 'Português (Portugal)', emoji: '🇵🇹' },
  ur: { code: 'ur', name: 'Urdu', nativeName: 'اردو', emoji: '🇵🇰', rtl: true },
}

export const SPEAKER_CAPTION_LANGUAGE_CODES = [
  'en',
  MULTILINGUAL_SPEAKER_LANGUAGE,
  'af',
  'ar',
  'hy',
  'as',
  'be',
  'bn',
  'bs',
  'bg',
  'ca',
  'zh-Hans',
  'zh-Hant',
  'yue',
  'hr',
  'cs',
  'da',
  'nl',
  'nl-BE',
  'et',
  'fi',
  'fr',
  'fr-CA',
  'ka',
  'de',
  'de-CH',
  'el',
  'gu',
  'he',
  'hi',
  'hu',
  'id',
  'it',
  'ja',
  'kn',
  'ko',
  'lv',
  'lt',
  'mk',
  'ms',
  'mr',
  'mn',
  'ne',
  'no',
  'ps',
  'fa',
  'pl',
  'pt',
  'pt-PT',
  'pt-BR',
  'pa',
  'ro',
  'ru',
  'sr',
  'sk',
  'sl',
  'es',
  'es-419',
  'sv',
  'fil',
  'ta',
  'te',
  'th',
  'tr',
  'uk',
  'ur',
  'vi',
] as const

function toPublicSpeakerLanguageMetadata(code: string): PublicSpeakerLanguageMetadata {
  const override = SPEAKER_LANGUAGE_METADATA_OVERRIDES[code]
  if (override) return override

  const targetLanguage = TARGET_CAPTION_LANGUAGE_BY_CODE.get(code)
  if (!targetLanguage) {
    throw new Error(`No public speaker language metadata configured for: ${code}`)
  }

  return {
    code: targetLanguage.code,
    name: targetLanguage.name,
    nativeName: targetLanguage.nativeName,
    emoji: targetLanguage.emoji,
    ...(targetLanguage.rtl === true ? { rtl: true } : {}),
  }
}

export const SPEAKER_CAPTION_LANGUAGES: readonly PublicCaptionLanguage[] =
  SPEAKER_CAPTION_LANGUAGE_CODES.map(toPublicSpeakerLanguageMetadata)

export function getPublicSpeakerCaptionLanguage(
  code: string | null | undefined,
): PublicCaptionLanguage | null {
  if (!code) return null
  return SPEAKER_CAPTION_LANGUAGES.find((language) => language.code === code) || null
}

export function createCaptionLanguageMap(
  ...languageLists: Array<readonly PublicCaptionLanguage[]>
): Map<string, PublicCaptionLanguage> {
  const languagesByCode = new Map<string, PublicCaptionLanguage>()

  for (const languageList of languageLists) {
    for (const language of languageList) {
      const code = language.code.trim()
      if (!code || languagesByCode.has(code)) continue
      languagesByCode.set(code, language)
    }
  }

  return languagesByCode
}

export function mergeCaptionLanguages(
  ...languageLists: Array<readonly PublicCaptionLanguage[]>
): PublicCaptionLanguage[] {
  return Array.from(createCaptionLanguageMap(...languageLists).values())
}

export function isTargetCaptionLanguageCode(code: string | null | undefined): boolean {
  if (!code) return false
  return TARGET_CAPTION_LANGUAGE_BY_CODE.has(code.trim())
}

export function isSpeakerCaptionLanguageCode(code: string | null | undefined): boolean {
  if (!code) return false
  return SPEAKER_CAPTION_LANGUAGE_CODES.some((languageCode) => languageCode === code.trim())
}

export function isTargetCaptionLanguageSelectionCode(
  code: string | null | undefined,
): boolean {
  if (!code) return false
  const normalized = code.trim()
  return normalized === ALL_TARGET_LANGUAGES || isTargetCaptionLanguageCode(normalized)
}

/**
 * Product-language equivalence used when a generic source language and a
 * region-specific target describe the same speech. This is not an alias:
 * both inputs must already be canonical catalog codes.
 */
export function isEquivalentSourceTargetLanguage(
  speakerLanguage: string | null | undefined,
  targetLanguage: string | null | undefined,
): boolean {
  const source = speakerLanguage?.trim()
  const target = targetLanguage?.trim()
  if (!source || !target) return false

  return (
    source === target ||
    EQUIVALENT_TARGET_LANGUAGE_BY_SPEAKER_LANGUAGE[source] === target
  )
}

export function getUnsupportedTargetCaptionLanguageCodes(
  targetLanguages: readonly string[] | null | undefined,
): string[] {
  if (!Array.isArray(targetLanguages)) return []

  const unsupported = new Set<string>()
  for (const rawCode of targetLanguages) {
    const code = rawCode.trim()
    if (!code) continue
    if (!isTargetCaptionLanguageSelectionCode(code)) {
      unsupported.add(code)
    }
  }

  return Array.from(unsupported)
}

export function getUnsupportedFeaturedTargetCaptionLanguageCodes(
  featuredLanguages: readonly string[] | null | undefined,
): string[] {
  if (!Array.isArray(featuredLanguages)) return []

  const unsupported = new Set<string>()
  for (const rawCode of featuredLanguages) {
    const code = rawCode.trim()
    if (!code) continue
    if (!isTargetCaptionLanguageCode(code)) {
      unsupported.add(code)
    }
  }

  return Array.from(unsupported)
}

export type CaptionLanguageDisplayInfo = {
  code?: string | null
  name?: string | null
  nativeName?: string | null
}

export type PublicCaptionLanguageDisplayNames = {
  primaryName: string
  secondaryName: string
}

function cleanLanguageDisplayValue(value: string | null | undefined): string {
  return value?.trim() || ''
}

function normalizeLanguageSearchValue(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
}

export function isMultilingualSpeakerLanguage(code: string | null | undefined): boolean {
  return code === MULTILINGUAL_SPEAKER_LANGUAGE
}

export function isCaptionFloorAudioEligible(
  selectedLanguage: string | null | undefined,
  speakerLanguage: string | null | undefined,
): boolean {
  return Boolean(
    selectedLanguage &&
      speakerLanguage &&
      selectedLanguage === speakerLanguage &&
      !isMultilingualSpeakerLanguage(speakerLanguage),
  )
}

export function getNativeCaptionLanguageName(
  code: string | null | undefined,
): string {
  if (!code) return ''
  return TARGET_CAPTION_LANGUAGE_BY_CODE.get(code.trim())?.nativeName || ''
}

export function getPublicCaptionLanguageLabel(
  language: CaptionLanguageDisplayInfo | null | undefined,
): string {
  return getPublicCaptionLanguageDisplayNames(language).primaryName
}

export function getPublicCaptionLanguageDisplayNames(
  language: CaptionLanguageDisplayInfo | null | undefined,
): PublicCaptionLanguageDisplayNames {
  if (!language) {
    return { primaryName: '', secondaryName: '' }
  }

  const nativeName = cleanLanguageDisplayValue(language.nativeName)
  const name = cleanLanguageDisplayValue(language.name)
  const code = cleanLanguageDisplayValue(language.code)
  const primaryName = nativeName || name || code
  const nativeMatchesName =
    normalizeLanguageSearchValue(nativeName) === normalizeLanguageSearchValue(name)
  const secondaryName =
    nativeName && name && !nativeMatchesName ? name : ''

  return { primaryName, secondaryName }
}

export function matchesPublicCaptionLanguageQuery(
  language: CaptionLanguageDisplayInfo | null | undefined,
  query: string,
): boolean {
  const normalizedQuery = normalizeLanguageSearchValue(query.trim())
  if (!normalizedQuery) return true
  if (!language) return false

  const { primaryName, secondaryName } = getPublicCaptionLanguageDisplayNames(language)
  const searchValues = [
    primaryName,
    secondaryName,
    cleanLanguageDisplayValue(language.nativeName),
    cleanLanguageDisplayValue(language.name),
    cleanLanguageDisplayValue(language.code),
  ]

  return searchValues.some((value) =>
    normalizeLanguageSearchValue(value).includes(normalizedQuery),
  )
}

export function getAvailableCaptionLanguages(
  speakerLanguage: string,
  targetLanguages: string[],
): string[] {
  const expandedTargetLanguages = expandTargetLanguages(
    targetLanguages,
    speakerLanguage,
  )
  if (isMultilingualSpeakerLanguage(speakerLanguage)) {
    return expandedTargetLanguages
  }

  return Array.from(
    new Set(
      [speakerLanguage, ...expandedTargetLanguages].filter((language) => !!language),
    ),
  )
}

export function getDefaultCaptionLanguage(
  speakerLanguage: string,
  targetLanguages: string[],
): string {
  const fallbackTargetLanguage =
    expandTargetLanguages(targetLanguages, speakerLanguage)[0] || ''

  if (isMultilingualSpeakerLanguage(speakerLanguage)) {
    return fallbackTargetLanguage
  }

  return speakerLanguage || fallbackTargetLanguage || DEFAULT_CAPTION_LANGUAGE
}

export function resolveStatusLanguage(
  previousSelectedLanguage: string,
  speakerLanguage: string,
  targetLanguages: string[],
): { languageToUse: string; languageChangedAutomatically: boolean } {
  const allLanguages = getAvailableCaptionLanguages(speakerLanguage, targetLanguages)
  const selectedLanguageAvailable = allLanguages.includes(previousSelectedLanguage)
  const languageToUse = selectedLanguageAvailable
    ? previousSelectedLanguage
    : getDefaultCaptionLanguage(speakerLanguage, targetLanguages)

  return {
    languageToUse,
    languageChangedAutomatically: previousSelectedLanguage !== languageToUse,
  }
}

export function orderPublicCaptionLanguageOptions<
  TLanguage extends { code: string },
>(
  languages: readonly TLanguage[],
  selectedLanguageCode: string | null | undefined,
  featuredLanguageCodes: readonly string[] | null | undefined,
): TLanguage[] {
  const byCode = new Map<string, TLanguage>()
  for (const language of languages) {
    if (!byCode.has(language.code)) {
      byCode.set(language.code, language)
    }
  }

  const orderedCodes: string[] = []
  const pushCode = (code: string | null | undefined) => {
    const normalized = code?.trim()
    if (!normalized || orderedCodes.includes(normalized) || !byCode.has(normalized)) {
      return
    }
    orderedCodes.push(normalized)
  }

  pushCode(selectedLanguageCode)
  if (Array.isArray(featuredLanguageCodes)) {
    featuredLanguageCodes.forEach(pushCode)
  }
  languages.forEach((language) => pushCode(language.code))

  return orderedCodes
    .map((code) => byCode.get(code))
    .filter((language): language is TLanguage => Boolean(language))
}

export function isAllTargetLanguagesSelection(
  targetLanguages: readonly string[] | null | undefined,
): boolean {
  return (
    Array.isArray(targetLanguages) &&
    targetLanguages.some((code) => code.trim() === ALL_TARGET_LANGUAGES)
  )
}

export function expandTargetLanguages(
  targetLanguages: readonly string[],
  speakerLanguage: string,
  availableTargetLanguages: readonly PublicCaptionLanguage[] = TARGET_CAPTION_LANGUAGES,
): string[] {
  const normalizedSpeakerLanguage = speakerLanguage.trim()

  if (isAllTargetLanguagesSelection(targetLanguages)) {
    return availableTargetLanguages
      .map((language) => language.code)
      .filter((code) => !isEquivalentSourceTargetLanguage(normalizedSpeakerLanguage, code))
  }

  return Array.from(
    new Set(
      targetLanguages
        .map((code) => code.trim())
        .filter(
          (code) => code && !isEquivalentSourceTargetLanguage(normalizedSpeakerLanguage, code),
        ),
    ),
  )
}

export function compactTargetLanguagesForStorage(
  targetLanguages: readonly string[],
  speakerLanguage: string,
  availableTargetLanguages: readonly PublicCaptionLanguage[] = TARGET_CAPTION_LANGUAGES,
): string[] {
  if (isAllTargetLanguagesSelection(targetLanguages)) {
    return [ALL_TARGET_LANGUAGES]
  }

  const normalized = expandTargetLanguages(
    targetLanguages,
    speakerLanguage,
    availableTargetLanguages,
  )

  if (normalized.length === 0) {
    return []
  }

  const allTargets = expandTargetLanguages(
    [ALL_TARGET_LANGUAGES],
    speakerLanguage,
    availableTargetLanguages,
  )
  if (normalized.length !== allTargets.length) {
    return normalized
  }

  const allSet = new Set(allTargets)
  return normalized.every((code) => allSet.has(code))
    ? [ALL_TARGET_LANGUAGES]
    : normalized
}
